import { Controller } from "@hotwired/stimulus"

/**
 * Draws one chart on a dashboard (SMA-110).
 *
 *   <div data-controller="dashboard-chart"
 *        data-dashboard-chart-kind-value="bar"
 *        data-dashboard-chart-labels-value='["1 Sep","2 Sep"]'
 *        data-dashboard-chart-datasets-value='[{"label":"Received","series":"primary","data":[3,5]}]'
 *        class="h-72"><canvas data-dashboard-chart-target="canvas"></canvas></div>
 *
 * Two things this does that a plain `new Chart(...)` would not.
 *
 * Chart.js is imported here rather than at the top of the module, so the ~200KB it weighs is
 * fetched by the pages that draw a chart and by no others: controllers/index.js is bundled into
 * the application entrypoint, which the layout loads on every request. Only the pieces these
 * charts use are registered, rather than `...registerables`.
 *
 * Colours come from dashboards.css through getComputedStyle, keyed on each dataset's `series`.
 * A hex here and a `bg-green-500` in a template would be one decision written twice, and the CSS
 * bars beside these charts would drift from them. Chart.js paints to a canvas and cannot restyle
 * itself, so the chart is rebuilt when the theme flips, on the `theme:changed` event the app
 * already dispatches for the workflow drawings.
 */
export default class extends Controller {
  static targets = ["canvas"]
  static values = { kind: String, labels: Array, datasets: Array, options: Object }

  connect() {
    this.live = true
    this.onTheme = () => this.build()
    document.addEventListener("theme:changed", this.onTheme)
    this.build()
  }

  disconnect() {
    this.live = false
    document.removeEventListener("theme:changed", this.onTheme)
    this.chart?.destroy()
    this.chart = null
  }

  async build() {
    // The palette is read fresh, because a build is also what a theme change asks for.
    this.resolved = null

    let Chart
    try {
      Chart = await this.library()
    } catch {
      // The figures beside the canvas are still on the page, so a chart that cannot be drawn costs
      // the reader the picture and not the numbers.
      return
    }

    // The controller can have gone by the time the import resolves: the first visit to a dashboard
    // fetches the chart chunk, and choosing a brief before it lands navigates away. Building then
    // would leave a Chart on a detached canvas, held in Chart.js's registry with its listeners.
    if (!this.live || !this.hasCanvasTarget) return

    this.chart?.destroy()
    this.chart = new Chart(this.canvasTarget, {
      type: this.kindValue,
      data: { labels: this.labelsValue, datasets: this.datasetsValue.map((set) => this.dataset(set)) },
      options: this.options()
    })
  }

  // One import for the whole page: the second chart on it gets the resolved module. A rejection is
  // not kept, or one failed chunk fetch would blank every chart on every later navigation until a
  // full reload, each one an unhandled rejection.
  library() {
    if (!this.constructor.chartjs) {
      this.constructor.chartjs = import("chart.js").then((module) => {
        const { Chart, BarController, LineController, DoughnutController, BarElement, LineElement,
                PointElement, ArcElement, CategoryScale, LinearScale, Tooltip, Legend, Filler } = module

        Chart.register(BarController, LineController, DoughnutController, BarElement, LineElement,
                       PointElement, ArcElement, CategoryScale, LinearScale, Tooltip, Legend, Filler)
        return Chart
      }).catch((error) => {
        this.constructor.chartjs = null
        throw error
      })
    }

    return this.constructor.chartjs
  }

  // A dataset as the server declared it, with its colours resolved. `series` names one colour for
  // a bar or a line; `seriesPer` names one per slice, which is what a doughnut needs.
  dataset(set) {
    const colours = set.seriesPer ? set.seriesPer.map((name) => this.colour(name)) : this.colour(set.series)
    const line = set.type === "line"

    return {
      ...set,
      backgroundColor: colours,
      ...(line
        ? { borderColor: colours, borderWidth: 2, pointRadius: 2, tension: 0.3 }
        : { borderWidth: 0, borderRadius: 2 })
    }
  }

  colour(name) {
    return this.styles.getPropertyValue(`--series-${name}`).trim() || this.styles.getPropertyValue("--series-primary").trim()
  }

  // Read once per build: each lookup is a style resolution, and a stacked chart asks for several.
  get styles() {
    this.resolved ||= getComputedStyle(this.element.closest(".dashboard") || this.element)
    return this.resolved
  }

  // The page's options over the defaults, merged one level into `scales` as well as at the top.
  // A shallow merge replaced the whole scales object, so any chart that set an axis lost the
  // themed gridline and tick colours with it and drew Chart.js's near-black default on a dark card,
  // which is the one thing --series-grid exists to prevent.
  options() {
    const defaults = this.defaults()
    const given = this.optionsValue

    return {
      ...defaults,
      ...given,
      scales: this.mergeScales(defaults.scales, given.scales)
    }
  }

  mergeScales(defaults, given) {
    if (!defaults) return given

    return Object.fromEntries(
      Object.entries(defaults).map(([ axis, options ]) => [ axis, { ...options, ...(given?.[axis] || {}) } ])
    )
  }

  defaults() {
    const ink = this.colour("ink")
    const grid = this.colour("grid")

    return {
      responsive: true,
      maintainAspectRatio: false,
      color: ink,
      plugins: { legend: { position: "bottom", labels: { boxWidth: 10, color: ink } } },
      scales: this.kindValue === "doughnut" ? undefined : {
        x: { grid: { display: false, color: grid }, ticks: { color: ink, maxTicksLimit: 8 } },
        y: { beginAtZero: true, grid: { color: grid }, ticks: { color: ink, precision: 0 } }
      }
    }
  }
}
